import { Location } from 'history';
import React, { useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { Dialog, Typography } from '@guildeducationinc/recess';
import { clickable } from '@src/analytics';

interface Props {
  when?: boolean | undefined;
}

const RouteLeavingGuard: React.FC<Props> = ({ when }) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const history = useHistory();
  const currentLocation = useLocation();

  // @todo - figure out why the typedef for this function is incompatible with Prompt['message']
  const handleBlockedNavigation: any = (nextLocation: Location): boolean => {
    if (currentLocation.pathname === nextLocation.pathname) return false;

    if (!confirmedNavigation) {
      setDialogVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const blockedNavigationClickable = (text: string) => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'account_profile',
      text,
      type: 'button icon',
    });
  };

  const handleConfirmNavigationClick = () => {
    blockedNavigationClickable('Leave');
    setDialogVisible(false);
    setConfirmedNavigation(true);
  };

  const handleCancelNavigationClick = () => {
    blockedNavigationClickable('Cancel');
    setDialogVisible(false);
  };

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    if (when) {
      return (event.returnValue = true);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  });

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      history.push(lastLocation);
    }
  }, [confirmedNavigation, lastLocation, history]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      <Dialog
        size='sm'
        title='Are you sure you want to leave?'
        content={<Typography variant='body1'>Your changes won&rsquo;t be saved.</Typography>}
        isOpen={dialogVisible}
        onClose={() => handleCancelNavigationClick}
        hideClose={true}
        className='xsDialog'
        testid='confirm-navigation-dialog'
        buttonA={{
          appearance: 'outline',
          intent: 'default',
          text: 'Cancel',
          onClick: handleCancelNavigationClick,
          testid: 'cancel-navigation',
        }}
        buttonB={{
          appearance: 'primary',
          intent: 'default',
          text: 'Leave',
          onClick: handleConfirmNavigationClick,
          testid: 'confirm-navigation',
        }}
      />
    </>
  );
};

export default RouteLeavingGuard;
